// useRecentItems.ts — tracks the last N visited entity-detail pages and
// persists them to localStorage for the command palette Recents group.
//
// Rules:
//  - Only detail pages match (list pages and /new pages are excluded).
//  - Entries are deduplicated by path; a revisit moves the entry to the top.
//  - At most MAX_STORED entries are persisted; only MAX_DISPLAY are returned.
//  - All localStorage access is wrapped in try/catch (quota / blocked).

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

/** Namespaced key — use this constant; never inline the string. */
export const PALETTE_RECENTS_KEY = 'vharta.palette.recents'

/** Maximum entries written to localStorage. */
const MAX_STORED = 10

/** Maximum entries surfaced in the palette. */
const MAX_DISPLAY = 5

export interface RecentItem {
  /** Full pathname (no search / hash). */
  path: string
  /** Human-readable label derived from the route pattern. */
  label: string
  /** Unix epoch ms — used for display ordering (newest first). */
  ts: number
}

// ── Detail route patterns ─────────────────────────────────────────────────────
//
// Each entry matches a detail page pathname and assigns a human-readable type
// label.  The negative lookahead (?!new$) excludes create-form routes from
// being recorded as "recents" (a creation form is not an entity detail).

interface DetailPattern {
  pattern: RegExp
  label: string
}

const DETAIL_PATTERNS: DetailPattern[] = [
  { pattern: /^\/tenant\/build\/workflows\/(?!new$)([^/]+)$/, label: 'Workflow' },
  { pattern: /^\/tenant\/build\/agent-graphs\/(?!new$)([^/]+)$/, label: 'Agent Graph' },
  { pattern: /^\/tenant\/build\/chains\/([^/]+)$/, label: 'Chain' },
  { pattern: /^\/tenant\/build\/runbooks\/(?!new$)([^/]+)$/, label: 'Runbook' },
  { pattern: /^\/tenant\/build\/templates\/([^/]+)$/, label: 'Template' },
  { pattern: /^\/tenant\/build\/agents\/([^/]+)$/, label: 'Agent' },
  { pattern: /^\/tenant\/build\/skills\/([^/]+)$/, label: 'Skill' },
  { pattern: /^\/tenant\/build\/teams\/([^/]+)$/, label: 'Team' },
  { pattern: /^\/tenant\/executions\/([^/]+)$/, label: 'Run' },
]

function matchDetailRoute(pathname: string): string | null {
  for (const { pattern, label } of DETAIL_PATTERNS) {
    if (pattern.test(pathname)) return label
  }
  return null
}

// ── Storage helpers ───────────────────────────────────────────────────────────

function readFromStorage(): RecentItem[] {
  try {
    const raw = localStorage.getItem(PALETTE_RECENTS_KEY)
    if (!raw) return []
    return JSON.parse(raw) as RecentItem[]
  } catch {
    return []
  }
}

function writeToStorage(items: RecentItem[]): void {
  try {
    localStorage.setItem(PALETTE_RECENTS_KEY, JSON.stringify(items))
  } catch {
    // localStorage may be full or blocked in private-browsing contexts
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Tracks the last visited entity-detail pages and returns the most recent
 * MAX_DISPLAY entries for the palette Recents group.
 *
 * Must be mounted inside the React Router tree (requires useLocation).
 */
export function useRecentItems(): RecentItem[] {
  const { pathname } = useLocation()

  // Initialise from storage so recents are available on first open even when
  // the user hasn't navigated yet in this session.
  const [display, setDisplay] = useState<RecentItem[]>(() =>
    readFromStorage().slice(0, MAX_DISPLAY)
  )

  useEffect(() => {
    const typeLabel = matchDetailRoute(pathname)
    if (!typeLabel) return

    const item: RecentItem = { path: pathname, label: typeLabel, ts: Date.now() }
    const stored = readFromStorage()
    // Dedupe by path (most recent visit wins), prepend, cap.
    const updated = [item, ...stored.filter((r) => r.path !== item.path)].slice(0, MAX_STORED)
    writeToStorage(updated)
    setDisplay(updated.slice(0, MAX_DISPLAY))
  }, [pathname])

  return display
}
